/** Monaco editor instance type */
import type * as monaco from 'monaco-editor';
import type { DatabaseEngineId } from '@/core/engines';
import type { EditorSelectionRange } from '@/types/aiWorkspace';
import type {
  IntelliSenseStatus,
  LanguageServiceDiagnostic,
} from '../../../connections/services/language-services/languageServiceProvider';

/**
 * The Monaco editor module namespace (structurally the same value
 * `@monaco-editor/react` hands to `onMount`: `typeof import('monaco-editor')`).
 *
 * It is intersected with two widened call signatures because monaco-editor's
 * bundled `.d.ts` is stricter than its actual runtime contract on exactly the
 * two surfaces this app's existing Monarch-grammar / completion-kind helpers
 * use (`setMonarchTokensProvider` with the app's runtime-valid grammar objects;
 * reverse-indexing the `CompletionItemKind` enum object by string name). The
 * intersection keeps the type fully assignable to the real Monaco namespace
 * (so `@monaco-editor/react`'s `OnMount`/`BeforeMount` still accept it) while
 * accepting those runtime-valid calls.
 */
export type Monaco = Omit<typeof monaco, 'languages' | 'editor'> & {
  editor: Omit<typeof monaco.editor, 'setModelMarkers'> & {
    // `model` is intentionally `unknown` (not `ITextModel`) so the real monaco
    // namespace stays assignable to `Monaco` AND `Monaco` satisfies the
    // deliberately-loose `MonacoLike` contract in
    // `applyDiagnosticsSuppression.ts`, which also declares this param
    // `unknown`. Monaco validates the model at runtime; every call site in
    // this codebase passes a real `editor.getModel()` (an `ITextModel`).
    setModelMarkers(
      model: unknown,
      owner: string,
      markers: monaco.editor.IMarkerData[]
    ): void;
  };
  languages: Omit<typeof monaco.languages, 'setMonarchTokensProvider'> & {
    // Declared as a METHOD signature (not a function-typed property) so its
    // parameters are checked bivariantly: this keeps the real monaco namespace
    // assignable to `Monaco` (so `@monaco-editor/react`'s `OnMount`/`BeforeMount`
    // still accept our handlers) while also accepting this app's runtime-valid
    // Monarch grammar object literals — monaco's bundled `IMonarchLanguageRule`
    // tuple-arity typing rejects the equivalent shape it consumes fine at
    // runtime.
    setMonarchTokensProvider(
      languageId: string,
      languageDef: object | PromiseLike<object>
    ): monaco.IDisposable;
  };
};

/**
 * Minimal Monaco standalone code editor surface this app relies on. The real
 * `monaco.editor.IStandaloneCodeEditor` satisfies this structurally (it is what
 * `@monaco-editor/react`'s `onMount` hands back).
 */
export interface IStandaloneCodeEditor {
  getValue: () => string;
  getModel: () => monaco.editor.ITextModel | null;
  focus: () => void;
  setValue: (value: string) => void;
  executeEdits: (
    source: string | null | undefined,
    edits: monaco.editor.IIdentifiedSingleEditOperation[]
  ) => boolean;
  /** Push an undo-stop boundary (real Monaco `ICodeEditor` method). Used to
   *  bracket programmatic `executeEdits` calls into their own undo unit — see
   *  `executeEditsWithUndoStop.ts`. */
  pushUndoStop: () => boolean;
  getAction: (id: string) => monaco.editor.IEditorAction | null;
  addAction: (action: monaco.editor.IActionDescriptor) => monaco.IDisposable;
  getSelection: () => monaco.Selection | null;
  setSelection?: (selection: monaco.Selection | monaco.IRange) => void;
  onDidChangeCursorSelection?: monaco.IEvent<monaco.editor.ICursorSelectionChangedEvent>;
  /** True when the editor's text area has DOM focus (real Monaco method). */
  hasTextFocus: () => boolean;
  /** Programmatically invoke an editor action, e.g. `editor.action.triggerSuggest`. */
  trigger: (source: string, handlerId: string, payload?: unknown) => void;
  /** Typing-latency telemetry hooks (real Monaco events) — see editorInputLatency.ts. */
  onDidFocusEditorText: monaco.IEvent<void>;
  onDidBlurEditorText: monaco.IEvent<void>;
  onKeyDown: monaco.IEvent<monaco.IKeyboardEvent>;
  onDidChangeModelContent: monaco.IEvent<monaco.editor.IModelContentChangedEvent>;
  /** Editor DOM nodes (real Monaco methods) — the oversized-paste guard scopes its capture listener here. */
  getDomNode: () => HTMLElement | null;
  getContainerDomNode?: () => HTMLElement | null;
  getSelections?: () => monaco.Selection[] | null;
}

/** Handle exposed by SqlMonaco for external access to editor methods */
/** One Monaco model edit: `rangeLength` characters at `rangeOffset` became `text`. */
export type SqlMonacoContentChange = {
  readonly rangeOffset: number;
  readonly rangeLength: number;
  readonly text: string;
};

export type SqlMonacoContentChangeEvent = {
  readonly changes: readonly SqlMonacoContentChange[];
  readonly isFlush: boolean;
};

export interface SqlMonacoHandle {
  /** Get currently selected text, or empty string if no selection */
  getSelectedText: () => string;
  /** Get the current selection range, or null if selection is empty */
  getSelectionRange: () => EditorSelectionRange | null;
  /**
   * Get the cursor's absolute character offset in the buffer, or null if the
   * editor is not ready. Live read — unlike `onSelectionChange`, which is
   * debounced, this never lags Monaco. Execute paths use it to resolve the
   * statement under the cursor at action time.
   */
  getCursorOffset: () => number | null;
  /**
   * Monaco model version id, or null if the editor is not ready. Bumps on
   * every content edit and never on caret/selection moves — callers use it
   * as a cache key to skip re-reading/re-parsing an unchanged buffer.
   */
  getModelVersionId: () => number | null;
  /** Get the full editor content */
  /**
   * Full buffer text, or `undefined` when there is nothing to read yet (Monaco
   * not mounted — the owner's own copy is authoritative) or Monaco refuses to
   * hand it back (a model past its heap-operation threshold, see
   * monacoModelText.ts). Use `isBufferUnreadable` to tell the two apart.
   */
  getValue: () => string | undefined;
  /** True when a mounted model is past Monaco's heap-operation threshold: reads, saves and executes must refuse. */
  isBufferUnreadable: () => boolean;
  /** Set the full editor content (returns false if editor not ready) */
  setValue: (value: string) => boolean;
  /**
   * Undo the last change on the editor's own undo stack — the same command
   * Cmd/Ctrl+Z runs in the editor. Returns false when the editor is not ready.
   * Optional so hand-written test handles need not implement it.
   */
  undo?: () => boolean;
  /** Redo on the editor's own undo stack (Cmd/Ctrl+Shift+Z in the editor). Optional like `undo`. */
  redo?: () => boolean;
  /**
   * Apply a set of range-based edits through Monaco's `executeEdits`, tagged
   * with `source` so they land as a SINGLE undo stop that survives on the
   * editor's own undo stack (unlike `setValue`, which resets it). Returns false
   * if the editor is not ready. Edits are typed structurally — `{ range: {four
   * 1-based line/column fields}; text }` — so the shared Monaco handle carries
   * no dependency on any consumer's edit types (VQE's `MonacoSingleEditOperation`
   * satisfies this structurally). Used by the Visual Query Editor's inline apply
   * path to preserve the Query Editor's undo history (P12 Task 4).
   *
   * SEMANTIC: an external, programmatic splice applied through this method is
   * ALWAYS its own undo unit — the real `SqlMonaco` implementation brackets
   * the underlying Monaco `executeEdits` call with `pushUndoStop()` before and
   * after (see `executeEditsWithUndoStop.ts`), so the edit never coalesces
   * into the user's adjacent typing in either direction. Without this, Monaco
   * silently merges a programmatic edit into whichever undo stop the user's
   * last keystroke left open (P12 gate fix).
   *
   * Optional so existing full-handle test doubles don't need to stub it; the
   * real `SqlMonaco` always implements it and consumers optional-chain.
   */
  executeEdits?: (
    source: string,
    edits: ReadonlyArray<{
      readonly range: {
        readonly startLineNumber: number;
        readonly startColumn: number;
        readonly endLineNumber: number;
        readonly endColumn: number;
      };
      readonly text: string;
    }>
  ) => boolean;
  /** Set selection range (returns false if editor not ready) */
  setSelectionRange: (range: EditorSelectionRange) => boolean;
  /** Focus the editor (returns true if editor is ready and focused) */
  focus: () => boolean;
  /** True when Monaco's text area, rather than one of its widgets, has focus. */
  hasTextFocus: () => boolean;
  /** Format the SQL in the editor */
  formatSql: () => void;
  /**
   * Insert a VS Code snippet with tabstops (e.g., "${1:placeholder}").
   * Returns true if snippet was inserted, false if editor not ready.
   */
  insertSnippet: (snippet: string) => boolean;
  /**
   * Set error/warning markers on the editor model.
   * @param markers  Array of marker shapes to apply.
   * @param owner    Monaco marker owner string (default: `'package-errors'`).
   *                 Use a distinct owner (e.g. `'sql-runtime-error'`) so these
   *                 markers are independent from IntelliSense diagnostics.
   *                 Pass an empty array with the same owner to clear markers.
   */
  setMarkers: (markers: SqlMonacoMarker[], owner?: string) => void;
  /** Scroll to a specific line and optionally set cursor position */
  revealLine: (line: number, column?: number) => void;
  /**
   * Get pixel position of the current selection, relative to the editor container.
   * `top`/`left`/`height` describe the first (start) line of the selection.
   * `bottom` is the pixel bottom of the LAST (end) line of the selection — useful
   * for floating widgets that need to anchor below the entire selection, not just
   * the first line. Returns null if no selection or editor not ready.
   */
  getSelectionPixelPosition: () => {
    top: number;
    left: number;
    height: number;
    bottom: number;
  } | null;
  /** Add a view zone (inline panel) below a given line. Returns zone ID. */
  addViewZone: (afterLineNumber: number, heightInPx: number, domNode: HTMLDivElement) => string | null;
  /** Remove a previously added view zone by ID. */
  removeViewZone: (zoneId: string) => void;
  /** Get the line number at the end of the current selection. Returns null if no selection. */
  getSelectionEndLine: () => number | null;
  /**
   * Subscribe to Monaco scroll changes. Callbacks fire whenever the editor
   * viewport scrolls (vertically or horizontally). Returns an unsubscribe
   * function. Used by floating widgets that must re-anchor to selection pixel
   * coordinates as the user scrolls, so they "stick" to the selection instead
   * of floating free in viewport space.
   */
  onDidScrollChange: (callback: () => void) => () => void;
  /**
   * Subscribe to model content changes with their exact ranges (offsets in
   * the model BEFORE the change, ordered from the end of the document so they
   * apply in sequence). `isFlush` marks a whole-document `setValue`, which has
   * no ranges to follow. Returns an unsubscribe function. Optional so
   * hand-written test handles need not implement it.
   */
  onDidChangeContent?: (callback: (event: SqlMonacoContentChangeEvent) => void) => () => void;
  /**
   * Subscribe to Monaco layout changes — fires when the editor's container
   * size changes (e.g. window resize, side-panel toggle, OR the tab being
   * hidden/shown in a workspace tab group). Required alongside
   * `onDidScrollChange` because tab show/hide changes the layout without
   * emitting a scroll event, so floating widgets that only react to scroll
   * would otherwise fail to re-anchor when the tab becomes visible again.
   * Returns an unsubscribe function.
   */
  onDidLayoutChange: (callback: () => void) => () => void;
  /**
   * Get the total height in pixels of the editor's scrollable layout area
   * (the content region excluding the horizontal scrollbar). Used by floating
   * widgets to decide whether a selection is currently visible in the viewport.
   * Returns null if the editor is not ready.
   */
  getLayoutHeight: () => number | null;
  /**
   * Get the pixel top/left/height of a given logical line/column, relative to
   * the editor container. Used by floating widgets (e.g., the Peek panel) that
   * need to anchor to a captured line number even after the user's selection
   * has moved. Returns null when the editor is not ready or the line is not
   * currently rendered.
   */
  getPixelPositionForPosition: (
    lineNumber: number,
    column: number
  ) => { top: number; left: number; height: number } | null;
  /**
   * Highlight a source range as the "current statement" (the statement the
   * cursor is inside when the editor has multiple statements). Pass `null`
   * to clear the highlight. Uses a Monaco decoration styled via the
   * `.sql-studio-current-statement` CSS class.
   */
  setCurrentStatementHighlight: (range: EditorSelectionRange | null) => void;
  /**
   * Get the viewport-relative bounding rect of the editor's outer DOM node.
   * Used by floating widgets that need to escape the editor's `overflow-hidden`
   * wrapper (e.g. the Peek overlay rendered via React portal) — they convert
   * editor-relative pixel positions to viewport coordinates by adding this
   * rect's `top`/`left`. Returns null if the editor isn't ready.
   */
  getEditorRect: () => { top: number; left: number; width: number; height: number } | null;
  /**
   * Get the current cursor position (1-based line + column). Returns null when
   * the editor is not ready. Mirrors Monaco's `getPosition()` semantics.
   */
  getCursorPosition: () => { lineNumber: number; column: number } | null;
  /**
   * Subscribe to cursor-position changes. Fires on every cursor move (including
   * pure caret moves with no selection change). Returns an unsubscribe function.
   */
  onDidChangeCursorPosition: (
    callback: (position: { lineNumber: number; column: number }) => void
  ) => () => void;
}

/** Marker for Monaco editor error/warning decorations */
export type SqlMonacoMarker = {
  startLineNumber: number;
  startColumn: number;
  endLineNumber: number;
  endColumn: number;
  message: string;
  severity: 'error' | 'warning' | 'info';
};

/**
 * IntelliSense status type — an alias of the provider-side union rather than a
 * second copy of it: the two had already drifted apart once (`partial`, S7
 * item 5, existed provider-side while this copy still rejected it), and every
 * value on this path originates from a provider status payload.
 */
export type IntelliSenseStatusType = IntelliSenseStatus;

/** Word wrap setting type */
export type WordWrapSetting = 'on' | 'off';

/** Props for the SqlMonaco component */
export interface SqlMonacoProps {
  value: string;
  onChange: (value: string) => void;
  /** Engine ID for dialect-aware features (e.g., formatting) */
  engineId?: DatabaseEngineId;
  /** Connection ID for IntelliSense context */
  connectionId?: string;
  /** Database name for IntelliSense context */
  database?: string;
  className?: string;
  height?: string;
  /** Override the editor language (defaults to 'sql'). When set to a non-SQL language, SQL IntelliSense is disabled. */
  language?: string;
  /** Word wrap setting (defaults to global setting from appStore) */
  wordWrap?: WordWrapSetting;
  /** Callback when IntelliSense status changes */
  onIntelliSenseStatusChange?: (status: IntelliSenseStatusType, message?: string) => void;
  /**
   * Callback when editor selection OR cursor position changes.
   * - `range` / `selectedText` describe a non-empty selection (null / '' when empty).
   * - `cursorPosition` is emitted even when the selection is empty, so consumers
   *   can react to cursor-only moves (used by the "current statement" highlight).
   */
  onSelectionChange?: (payload: {
    range: EditorSelectionRange | null;
    selectedText: string;
    cursorPosition: { lineNumber: number; column: number; offset: number } | null;
  }) => void;
}

/** Extract connection ID from a document URI */
export function getConnectionIdFromDocumentUri(uri: string): string | null {
  const match = uri.match(/^file:\/\/\/sql-studio\/([^/]+)\/(?:(?!documents\/)[^/]+\/)?documents\//);
  if (!match?.[1] || match[1] === 'untitled') {
    return null;
  }
  return match[1];
}

/**
 * Shape of the JSON-RPC notification params the SQL Tools Service emits for the
 * notifications this app reacts to (`connection/complete`,
 * `textDocument/intelliSenseReady`, `textDocument/publishDiagnostics`). The
 * service is inconsistent about which URI field it populates, so all of these
 * are optional and consumers fall through them in order.
 */
export type SqlLanguageServerNotificationParams = {
  ownerUri?: string;
  uri?: string;
  connection?: { ownerUri?: string };
  textDocument?: { uri?: string };
  diagnostics?: LanguageServiceDiagnostic[];
};

// TypeScript interface for window.sqlLanguageServer
declare global {
  interface Window {
    // Must stay identical to the declaration in `src/types/electron.d.ts` —
    // declaration merging on `Window` rejects a differing type.
    sqlLanguageServer?: {
      // `sendRequest` has no callers; its params/result are opaque LSP JSON-RPC
      // payloads whose shape varies per `method`. Mirrors the codebase's own
      // `electronAPI.pgsqlLanguageServer` typing (params?: unknown / result: unknown).
      sendRequest: (method: string, params: unknown) => Promise<unknown>;
      onNotification: (
        callback: (method: string, params?: SqlLanguageServerNotificationParams) => void
      ) => () => void;
      // `details` is opaque per-status payload; current consumer (status pill
      // via SqlMonaco) ignores it, so it stays `unknown`.
      onStatusChange: (callback: (status: string, details?: unknown) => void) => () => void;
    };
  }
}
